/**
 * Report filter section presets — the accordion groups ReportFilterDrawer shows.
 *
 * One definition per report family, imported by both the live report pages
 * (through ReportFilterDrawer) and the scheduled-report editor (ScheduleFilterDrawer).
 * Before this, the two copies drifted: the Devices serial subtext reached only one of
 * them, Caregiver / Shift schedules lost their Response Time filter in the editor, and
 * Maintenance schedules offered filters the scheduled report never reads
 * (docs/DUPLICATION_AUDIT.md, B4).
 *
 * Section shape: { id, label, subs: [subDef] }; subDef is FilterSubSection's
 * (key, label, idKey, nameKey, optionsKey, subtextKey?, subtextLabel?), plus chipLabel
 * where the drawer's singular label would read wrong on a filter chip (utils/reportFilterChips.js). A section with
 * id 'events' also hosts the Response Time control, unless the drawer hides it.
 */

const AREA_TYPES = { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' };
const AREA_GROUPS = { key: 'areaGroups', label: 'Area Groups', idKey: 'area_group_id', nameKey: 'area_group_name', optionsKey: 'areaGroups' };
const AREAS = { key: 'areas', label: 'Area', chipLabel: 'Areas', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' };
const RESIDENTS = { key: 'residents', label: 'Resident', chipLabel: 'Residents', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' };
const RESIDENT_GROUPS = { key: 'residentGroups', label: 'Resident Groups', idKey: 'resident_group_id', nameKey: 'resident_group_name', optionsKey: 'residentGroups' };
const USERS = { key: 'users', label: 'User', chipLabel: 'Users', idKey: 'user_id', nameKey: 'display_name', optionsKey: 'users' };
const USER_GROUPS = { key: 'userGroups', label: 'User Groups', idKey: 'group_id', nameKey: 'group_name', optionsKey: 'userGroups' };
const DEVICE_CATEGORIES = { key: 'deviceCategories', label: 'Device Categories', idKey: 'device_category_id', nameKey: 'device_category_short_name', optionsKey: 'deviceCategories' };
const DEVICES = { key: 'devices', label: 'Devices', idKey: 'device_id', nameKey: 'device_name', subtextKey: 'manufacture_uuid', subtextLabel: 'Serial', optionsKey: 'devices' };
/* Keyed by cloud_reason_code_id: the page expands the selection to cloud + local ids
   (utils/reasonFilterIds.js), and reason_code_id is null for cloud-only reasons. */
const REASONS = { key: 'reasons', label: 'Reasons', idKey: 'cloud_reason_code_id', nameKey: 'reason_code_description', optionsKey: 'reasonCodes' };
const EVENT_TYPES = { key: 'eventTypes', label: 'Event Types', idKey: 'event_type_id', nameKey: 'event_type_name', optionsKey: 'eventTypes' };
const CALL_TYPES = { key: 'callTypes', label: 'Call Types', idKey: 'call_type_id', nameKey: 'call_type_name', optionsKey: 'callTypes' };
/* Check-in / rounds windows by id (docs/CHECKIN_WINDOW_FILTER_PLAN.md). Both store as
   `windows` (window_filter); each report lists only its own window type. The subtext is
   the window's times, plus "Inactive" for a window that is currently ignored
   (utils/reportFilterOptions.js fetchWindowOptions). */
const CHECKIN_WINDOWS = { key: 'windows', label: 'Check-in Windows', idKey: 'window_id', nameKey: 'description', subtextKey: 'window_times', optionsKey: 'checkinWindows' };
const ROUNDS_WINDOWS = { key: 'windows', label: 'Rounds Windows', idKey: 'window_id', nameKey: 'description', subtextKey: 'window_times', optionsKey: 'roundsWindows' };

/** Alert Details, Hourly, Executive, Caregiver, Shift (and their schedules). */
export const ALERT_REPORT_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [AREA_TYPES, AREA_GROUPS, AREAS] },
    { id: 'residents', label: 'Residents', subs: [RESIDENTS, RESIDENT_GROUPS] },
    { id: 'users', label: 'Users', subs: [USERS, USER_GROUPS] },
    { id: 'devices', label: 'Devices', subs: [DEVICE_CATEGORIES, DEVICES] },
    { id: 'events', label: 'Events', subs: [REASONS, EVENT_TYPES] },
];

/** Resident Check-in / Staff Rounds family. No users or events. */
export const WELLNESS_REPORT_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [AREA_TYPES, AREA_GROUPS, AREAS] },
    { id: 'residents', label: 'Residents', subs: [RESIDENTS, RESIDENT_GROUPS] },
    { id: 'devices', label: 'Devices', subs: [DEVICE_CATEGORIES] },
];

/** Check-in Analysis and Missed Check-in: the wellness groups plus the check-in windows,
    so a schedule can report on one window (e.g. an email at the end of Breakfast). */
export const CHECKIN_REPORT_SECTIONS = [
    { id: 'windows', label: 'Check-in Windows', subs: [CHECKIN_WINDOWS] },
    ...WELLNESS_REPORT_SECTIONS,
];

/** Rounds Analysis: the wellness groups plus the rounds windows. */
export const ROUNDS_REPORT_SECTIONS = [
    { id: 'windows', label: 'Rounds Windows', subs: [ROUNDS_WINDOWS] },
    ...WELLNESS_REPORT_SECTIONS,
];

/** Environment reports. Device category is set by the report type, so it is not offered.
    excludeNoReason is honoured by the schedule editor, whose options carry a synthetic
    "No Reason" entry the environmental report cannot filter on. */
export const ENVIRONMENTAL_REPORT_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [AREA_TYPES, AREAS] },
    { id: 'residents', label: 'Residents', subs: [RESIDENTS] },
    { id: 'events', label: 'Events', subs: [{ ...REASONS, excludeNoReason: true }] },
];

/** Maintenance: event types only (the report reads no other filter). */
export const MAINTENANCE_REPORT_SECTIONS = [
    { id: 'events', label: 'Events', subs: [EVENT_TYPES] },
];

/** Call Logs. */
export const CALL_LOGS_REPORT_SECTIONS = [
    { id: 'callTypes', label: 'Call Types', subs: [CALL_TYPES] },
];

/* Which preset a scheduled report type uses lives with the rest of the per-type facts:
   scheduleFilterConfig() in utils/reportTypes.js. */
